import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Camera, ScanLine, X, AlertCircle, CheckCircle, ScanBarcode } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '@/lib/api';

export default function BarcodeScannerDialog({ open, onClose, onScanResult }) {
  const [scanMode, setScanMode] = useState('choose'); // 'choose' | 'camera' | 'physical'
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [physicalInput, setPhysicalInput] = useState('');
  const scannerRef = useRef(null);
  const readerRef = useRef(null);
  const physicalInputRef = useRef(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const decodeCF = useCallback(async (cf) => {
    cf = cf.toUpperCase().replace(/\s/g, '');
    if (cf.length !== 16 || !/^[A-Z0-9]{16}$/.test(cf)) return { codice_fiscale: cf, raw: true };
    try {
      const res = await api.get(`/utility/decodifica-cf/${cf}`);
      return res.data;
    } catch {
      return { codice_fiscale: cf, raw: true };
    }
  }, []);

  const startScanner = useCallback(async () => {
    setError('');
    setResult(null);
    setScanning(true);
    await new Promise(r => setTimeout(r, 300));
    const el = document.getElementById('cf-barcode-reader');
    if (!el) { setError('Errore inizializzazione scanner'); setScanning(false); return; }
    try {
      const scanner = new Html5Qrcode('cf-barcode-reader');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 300, height: 100 }, aspectRatio: 3.0 },
        async (decodedText) => {
          const decoded = await decodeCF(decodedText);
          setResult(decoded);
          stopScanner();
        },
        () => {}
      );
    } catch {
      setError('Impossibile accedere alla fotocamera. Verifica i permessi.');
      setScanning(false);
    }
  }, [stopScanner, decodeCF]);

  useEffect(() => {
    if (!open) { stopScanner(); setResult(null); setError(''); setScanMode('choose'); setPhysicalInput(''); }
  }, [open, stopScanner]);

  // Auto-start camera when switching to camera mode
  useEffect(() => {
    if (open && scanMode === 'camera' && !result) {
      const t = setTimeout(startScanner, 200);
      return () => clearTimeout(t);
    }
  }, [open, scanMode, result, startScanner]);

  // Keep physical input focused
  useEffect(() => {
    if (open && scanMode === 'physical') {
      const t = setTimeout(() => { physicalInputRef.current?.focus(); }, 100);
      return () => clearTimeout(t);
    }
  }, [open, scanMode]);

  const handlePhysicalKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = physicalInput.trim();
      if (!val) return;
      const decoded = await decodeCF(val);
      setResult(decoded);
      setPhysicalInput('');
    }
  };

  const handleConfirm = () => {
    if (result && onScanResult) onScanResult(result);
    onClose();
  };

  const handleClose = () => { stopScanner(); setScanMode('choose'); setPhysicalInput(''); onClose(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="barcode-scanner-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-brand" />Scansione Codice Fiscale
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">

          {/* MODE: CHOOSE */}
          {scanMode === 'choose' && !result && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-500 text-center">
                Scegli come scansionare il codice fiscale dalla tessera sanitaria
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setScanMode('physical')}
                  className="flex flex-col items-center gap-2 p-5 border-2 border-orange-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all bg-orange-50/30"
                  data-testid="cf-choose-physical"
                >
                  <ScanBarcode className="w-8 h-8 text-orange-500" />
                  <span className="text-sm font-medium text-slate-700">Lettore Fisico</span>
                  <span className="text-[10px] text-slate-400">Scanner USB/Bluetooth</span>
                </button>
                <button
                  onClick={() => setScanMode('camera')}
                  className="flex flex-col items-center gap-2 p-5 border-2 border-slate-200 rounded-xl hover:border-brand hover:bg-blue-50/50 transition-all"
                  data-testid="cf-choose-camera"
                >
                  <Camera className="w-8 h-8 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700">Fotocamera</span>
                  <span className="text-[10px] text-slate-400">Inquadra il barcode</span>
                </button>
              </div>
            </div>
          )}

          {/* MODE: PHYSICAL SCANNER */}
          {scanMode === 'physical' && !result && (
            <div className="space-y-4 py-2">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-100 mb-1">
                  <ScanBarcode className="w-8 h-8 text-orange-600 animate-pulse" />
                </div>
                <p className="text-sm font-medium text-slate-700">Lettore Fisico Attivo</p>
                <p className="text-xs text-slate-400">Scansiona il codice a barre della tessera sanitaria con il lettore</p>
              </div>
              <div className="relative">
                <Input
                  ref={physicalInputRef}
                  value={physicalInput}
                  onChange={e => setPhysicalInput(e.target.value)}
                  onKeyDown={handlePhysicalKeyDown}
                  onBlur={() => { setTimeout(() => { physicalInputRef.current?.focus(); }, 50); }}
                  placeholder="In attesa di scansione CF..."
                  className="text-center text-lg font-mono border-2 border-orange-300 focus:border-orange-500 bg-orange-50/50 h-14 uppercase"
                  data-testid="cf-scanner-physical-input"
                  autoFocus
                  autoComplete="off"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setScanMode('camera'); setPhysicalInput(''); }} className="text-xs w-full">
                <Camera className="w-3.5 h-3.5 mr-1" />Passa a Fotocamera
              </Button>
            </div>
          )}

          {/* MODE: CAMERA */}
          {scanMode === 'camera' && !result && (
            <>
              <p className="text-xs text-slate-500">
                Inquadra il codice a barre presente sulla tessera sanitaria o sul documento del paziente.
              </p>
              <div className="relative bg-slate-900 rounded-lg overflow-hidden min-h-[200px]" ref={readerRef}>
                <div id="cf-barcode-reader" className="w-full" />
                {!scanning && !error && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <Camera className="w-10 h-10 text-slate-400" />
                    <p className="text-xs text-slate-400">Avvio fotocamera...</p>
                  </div>
                )}
              </div>
              {error && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { stopScanner(); setScanMode('physical'); }} className="text-xs w-full">
                    <ScanBarcode className="w-3.5 h-3.5 mr-1" />Usa Lettore Fisico
                  </Button>
                </div>
              )}
              {!error && (
                <Button variant="ghost" size="sm" onClick={() => { stopScanner(); setScanMode('physical'); }} className="text-xs text-slate-400 w-full">
                  <ScanBarcode className="w-3.5 h-3.5 mr-1" />Passa a Lettore Fisico
                </Button>
              )}
            </>
          )}

          {/* RESULT */}
          {result && (
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2" data-testid="scan-result">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-800">Codice Fiscale Rilevato</span>
              </div>
              <p className="text-sm font-mono font-bold text-slate-800">{result.codice_fiscale}</p>
              {!result.raw && (
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-2">
                  <div><span className="text-slate-400">Nascita:</span> <strong>{result.data_nascita}</strong></div>
                  <div><span className="text-slate-400">Sesso:</span> <Badge className="text-[10px] ml-1">{result.sesso === 'M' ? 'Maschio' : 'Femmina'}</Badge></div>
                  {result.luogo_nascita && <div><span className="text-slate-400">Luogo:</span> <strong>{result.luogo_nascita}</strong></div>}
                  {result.eta != null && <div><span className="text-slate-400">Eta:</span> <strong>{result.eta} anni</strong></div>}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              {result ? 'Inserimento Manuale' : 'Chiudi'}
            </Button>
            {scanning && (
              <Button variant="outline" onClick={stopScanner} className="text-red-600"><X className="w-4 h-4 mr-1" />Ferma</Button>
            )}
            {result && (
              <Button onClick={handleConfirm} data-testid="confirm-scan-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle className="w-4 h-4 mr-1" />Conferma e Compila
              </Button>
            )}
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            Ai sensi del GDPR (Reg. UE 2016/679) e del D.Lgs. 196/2003, i dati acquisiti tramite scansione
            vengono trattati esclusivamente per finalita assistenziali e sanitarie. La scansione non salva immagini del documento.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
